import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title_ar: string;
  title_en: string | null;
  body_ar: string | null;
  body_en: string | null;
  link: string | null;
  is_read: boolean;
  created_at: string;
}

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: async () => {
      if (!user) return [] as Notification[];
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data || []) as Notification[];
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (!user) return;
    const channel = supabase.channel(`notifications-${user.id}-${Math.random().toString(36).slice(2)}`);
    channel
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['notifications', user.id] });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  const unreadCount = (query.data || []).filter((n) => !n.is_read).length;

  const markAllRead = async () => {
    if (!user) return;
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false);
    queryClient.invalidateQueries({ queryKey: ['notifications', user.id] });
  };

  const markRead = async (id: string) => {
    await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
  };

  return { ...query, unreadCount, markAllRead, markRead };
}

// Unread booking-message counts grouped by booking_id, with realtime
export function useUnreadMessageCounts(bookingIds: string[]) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const key = ['unread-messages', user?.id, bookingIds.sort().join(',')];

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      if (!user || bookingIds.length === 0) return {} as Record<string, number>;
      const { data, error } = await supabase
        .from('booking_messages')
        .select('booking_id, sender_id, read_at')
        .in('booking_id', bookingIds)
        .is('read_at', null);
      if (error) throw error;
      const counts: Record<string, number> = {};
      (data || []).forEach((m: any) => {
        if (m.sender_id !== user.id) {
          counts[m.booking_id] = (counts[m.booking_id] || 0) + 1;
        }
      });
      return counts;
    },
    enabled: !!user && bookingIds.length > 0,
  });

  useEffect(() => {
    if (!user || bookingIds.length === 0) return;
    const channel = supabase.channel(`unread-msgs-${user.id}-${Math.random().toString(36).slice(2)}`);
    channel
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'booking_messages' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['unread-messages'] });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, bookingIds.join(','), queryClient]);

  return query;
}
